import React, { useRef } from "react";
import SimpleBar from "simplebar-react";
import { CanvasTemplate, TipName } from "shared/datamodel/schemas";
import style from "./side-menu-new.module.css";
import { TeamWithProjects } from "shared/datamodel/schemas/team";
import { HomeIcon } from "frontend/icons/home";
import { SideMenuSection } from "./side-menu-section";
import { MyCanvasesIcon } from "frontend/icons/my-canvases";
import { WorkspaceProjectHierarchy } from "./team-project-hierarchy";
import { SharedWithMeIcon } from "frontend/icons/shared-with-me";
import { TemplatesIcon } from "frontend/icons/templates";
import { BoardSectionType, BoardSection } from "shared/datamodel/schemas/board-section";
import WalkthroughTipPresenter from "frontend/walkthrough-tips/walkthrough-tip-presenter";
import { WalkthroughTipType } from "frontend/walkthrough-tips/walkthrough-tip-model";
import consts from "shared/consts";
import tracking from "frontend/tracking";

export default function SideMenuNew({
  selectedTeamId,
  setSelectedTeam,
  selectedProjectId,
  setSelectedProject,
  selectedSection,
  createNewBoard,
  onEditSeats,
  onMyCanvasesClick,
  onRecentCanvasesClick,
  onSharedWithMeClick,
  setShowRestrictedModal,
  handleNoPermissionToCreate,
  setShowTemplatesModal,
  setIsMovingBoards,
}: {
  selectedTeamId: string | null;
  setSelectedTeam: (team: TeamWithProjects) => void;
  selectedProjectId: string | null;
  setSelectedProject: (projectId: string, urlId?: string) => void;
  selectedSection: BoardSection;
  createNewBoard: ({
    template,
    inputProjectId,
    inputTeamId,
    source,
  }: {
    template: CanvasTemplate;
    inputProjectId: string | null;
    inputTeamId: string | null;
    source: string;
  }) => void;
  onEditSeats: () => void;
  onMyCanvasesClick: () => void;
  onRecentCanvasesClick: () => void;
  onSharedWithMeClick: () => void;
  setShowRestrictedModal: (show: boolean) => void;
  handleNoPermissionToCreate: () => void;
  setShowTemplatesModal: (show: boolean) => void;
  setIsMovingBoards: (value: boolean) => void;
}) {
  const sectionsRef = useRef<HTMLDivElement>(null);
  const simpleBarRef = useRef<HTMLDivElement>(null);

  return (
    <div className={style.container}>
      <div className={style.startContainer} ref={sectionsRef}>
        <SideMenuSection
          name={"Home"}
          icon={() => <HomeIcon />}
          onClick={onRecentCanvasesClick}
          isSelected={selectedSection.type === BoardSectionType.RECENT}
        />
        <SideMenuSection
          name={"My Canvases"}
          icon={() => <MyCanvasesIcon />}
          onClick={onMyCanvasesClick}
          isSelected={selectedSection.type === BoardSectionType.MY_BOARDS}
        />
        <SideMenuSection
          name={"Shared with me"}
          icon={() => <SharedWithMeIcon />}
          onClick={onSharedWithMeClick}
          isSelected={selectedSection.type === BoardSectionType.SHARED_WITH_ME}
        />
        <SideMenuSection
          name={"Templates"}
          icon={() => <TemplatesIcon />}
          onClick={() => {
            tracking.trackAnalyticsEvent("templates-library-clicked", {
              category: consts.TRACKING_CATEGORY.TEMPLATES,
              source: "side-menu",
              isNewHomePage: true,
            });
            setShowTemplatesModal(true);
          }}
          isSelected={selectedSection.type === BoardSectionType.TEMPLATES}
        />
      </div>
      <div className={style.divider}></div>
      <div className={style.centerContainer}>
        <SimpleBar
          scrollbarMaxSize={100}
          style={{
            maxHeight: "99%",
            width: "100%",
          }}
          scrollableNodeProps={{ ref: simpleBarRef }}
        >
          <WorkspaceProjectHierarchy
            selectedProjectId={selectedProjectId}
            selectedTeamId={selectedTeamId}
            setSelectedProject={setSelectedProject}
            onEditSeats={onEditSeats}
            setSelectedTeam={setSelectedTeam}
            setShowRestrictedModal={setShowRestrictedModal}
            handleNoPermissionToCreate={handleNoPermissionToCreate}
            createNewBoard={createNewBoard}
            resetSelectedInstance={onRecentCanvasesClick}
            simpleBarRef={simpleBarRef}
            setIsMovingBoards={setIsMovingBoards}
          />
        </SimpleBar>
      </div>
      <WalkthroughTipPresenter
        tipName={TipName.walkthroughHomePageSections}
        attachToRef={sectionsRef}
        side="left"
        type={WalkthroughTipType.HomePage}
      />
    </div>
  );
}
